import { SignalSubscription } from './SignalSubscription';

export interface SignalHandler<T> {
  (payload: T): void | Promise<void>;
}

export interface SignalSubscribeOptions {
  once?: boolean;
  priority?: number;
  context?: unknown;
}

export class Signal<T = void> {
  private handlers: Array<{
    fn: SignalHandler<T>;
    priority: number;
    once: boolean;
    context?: unknown;
    id: symbol;
  }> = [];

  private isEmitting = false;
  private pendingRemoves = new Set<symbol>();

  constructor(
    public readonly name: string,
    public readonly debugMode: boolean = false
  ) {}

  subscribe(
    handler: SignalHandler<T>,
    options: SignalSubscribeOptions = {}
  ): SignalSubscription {
    const { once = false, priority = 0, context } = options;

    const id = Symbol();
    const entry = { fn: handler, priority, once, context, id };

    const insertIndex = this.handlers.findIndex((h) => h.priority < priority);
    if (insertIndex === -1) {
      this.handlers.push(entry);
    } else {
      this.handlers.splice(insertIndex, 0, entry);
    }

    if (this.debugMode) {
      console.log(`[Signal:${this.name}] Subscribed (priority: ${priority})`);
    }

    return new SignalSubscription(() => this.unsubscribe(id));
  }

  once(
    handler: SignalHandler<T>,
    options: Omit<SignalSubscribeOptions, 'once'> = {}
  ): SignalSubscription {
    return this.subscribe(handler, { ...options, once: true });
  }

  async emit(payload: T): Promise<void> {
    if (this.debugMode) {
      console.log(`[Signal:${this.name}] Emit`, payload);
    }

    this.isEmitting = true;

    const handlersToCall = [...this.handlers];

    for (const handler of handlersToCall) {
      if (this.pendingRemoves.has(handler.id)) continue;

      try {
        const result = handler.fn.call(handler.context, payload);
        if (result instanceof Promise) {
          await result;
        }
      } catch (error) {
        console.error(`[Signal:${this.name}] Handler error:`, error);
      }

      if (handler.once) {
        this.pendingRemoves.add(handler.id);
      }
    }

    this.isEmitting = false;
    this.processPendingRemoves();
  }

  private unsubscribe(id: symbol): void {
    if (this.isEmitting) {
      this.pendingRemoves.add(id);
    } else {
      const index = this.handlers.findIndex((h) => h.id === id);
      if (index !== -1) {
        this.handlers.splice(index, 1);
        if (this.debugMode) {
          console.log(`[Signal:${this.name}] Unsubscribed`);
        }
      }
    }
  }

  private processPendingRemoves(): void {
    if (this.pendingRemoves.size > 0) {
      this.handlers = this.handlers.filter(
        (h) => !this.pendingRemoves.has(h.id)
      );
      this.pendingRemoves.clear();
    }
  }

  clear(): void {
    this.handlers = [];
    this.pendingRemoves.clear();
    if (this.debugMode) {
      console.log(`[Signal:${this.name}] Cleared all handlers`);
    }
  }

  get subscriberCount(): number {
    return this.handlers.length;
  }
}
